import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Content,
  Flex,
  FlexItem,
  Modal,
  ModalBody,
  ModalHeader,
  Spinner,
  Wizard,
  WizardStep,
} from '@patternfly/react-core';
import type { WizardStepType } from '@patternfly/react-core';
import type { OrganizationDomain } from '../../../types/models/OrganizationDomain';
import type {
  OrganizationDomainResources,
  OrganizationDomainTeardownResult,
} from '../../../types/models/OrganizationDomainResources';
import { useDkimSelectors } from '../../../hooks/useDkimSelectors';
import { useOrganizationDomainTeardown } from '../../../hooks/useOrganizationDomainTeardown';
import { useNotifications } from '../../../contexts/NotificationContext';
import { DOMAIN_RESOURCE_TYPES } from './domainResourceTypes';
import type { DomainResourceType, DomainResourceTypeKey } from './domainResourceTypes';
import {
  EMPTY_WIZARD_SELECTION,
  buildInventory,
  crossTypeViolations,
  deriveReview,
  hasAnyAction,
  mailDomainGoes as mailDomainGoesOf,
  orgDomainBlockers as orgDomainBlockersOf,
  reconcileInventory,
  toTeardownRequest,
  toggleAllElements,
  toggleAllTypes,
  toggleElement,
  toggleOrgDomain,
  toggleType,
  typeState,
} from './deleteResourcesWizardSelection';
import type { DeleteResourcesSelection } from './deleteResourcesWizardSelection';
import type { ResourceStepProps } from './resourceStep';
import ResourceTypeSelectionStep from './ResourceTypeSelectionStep';
import DnsZoneStep from './DnsZoneStep';
import DomainRegistrationStep from './DomainRegistrationStep';
import EmailHostingStep from './EmailHostingStep';
import TlsCertificatesStep from './TlsCertificatesStep';
import DeleteResourcesReviewStep from './DeleteResourcesReviewStep';
import DeleteResourcesReviewFooter from './DeleteResourcesReviewFooter';
import DeleteResourcesResult from './DeleteResourcesResult';

/** Stable step ids — shared between the `<WizardStep>` definitions and the review step's Edit links. */
export const DELETE_RESOURCES_STEP_IDS = {
  selection: 'delete-resources-selection',
  review: 'delete-resources-review',
  /** One step per resource type, hidden unless that type is selected. */
  type: (key: DomainResourceTypeKey): string => `delete-resources-type-${key}`,
} as const;

/**
 * The step body of each resource type. Exhaustive over `DomainResourceTypeKey`, like the registry
 * itself: adding a type to the union fails to compile until both its registry entry and its step body
 * exist. A type whose per-element listing is not built yet points at `ResourceStepPlaceholder`.
 */
const STEP_COMPONENTS: Record<DomainResourceTypeKey, React.ComponentType<ResourceStepProps>> = {
  dns_zone: DnsZoneStep,
  domain_registration: DomainRegistrationStep,
  email_hosting: EmailHostingStep,
  tls_certificates: TlsCertificatesStep,
};

interface DeleteResourcesWizardProps {
  domain: OrganizationDomain;
  resources: OrganizationDomainResources;
  onCancel: () => void;
  /**
   * Re-reads the resource inventory. Called when the user chooses to delete more after a run: the run
   * changed what is hosted, so the wizard restarts from the fresh aggregate rather than from a stale
   * selection.
   */
  onRefetchResources?: () => void;
  /** Injectable for the tests; defaults to the real registry. */
  types?: readonly DomainResourceType[];
}

/**
 * The "Delete resources" wizard: step 1 picks the resource types, one step per selected type lists its
 * concrete elements, and the review step splits *Will be deleted* / *Will be kept* before the
 * confirmation. Design: `docs/plans/org-domain-delete-resources-wizard.md` §5.
 *
 * All selection state is held **here**, above `<Wizard>`: PatternFly unmounts inactive step bodies, so
 * a step cannot own its own state. Steps are derived from the registry — a new resource type appears by
 * adding a registry entry and a step body, never by editing this list.
 *
 * Confirming submits the whole selection to `POST /organization/domains/{id}/teardown` — one request,
 * so the browser never fans out destructive calls and the backend owns the ordering. A pre-flight
 * refusal stays **inside** the wizard, above the footer, so the selection can be fixed; a run that
 * executed replaces the wizard with the per-element result (§5.4).
 */
const DeleteResourcesWizard: React.FunctionComponent<DeleteResourcesWizardProps> = ({
  domain,
  resources,
  onCancel,
  onRefetchResources,
  types = DOMAIN_RESOURCE_TYPES,
}) => {
  const inventory = useMemo(() => buildInventory(resources, types), [resources, types]);
  const [selection, setSelection] = useState<DeleteResourcesSelection>(EMPTY_WIZARD_SELECTION);

  // A refetch (retry after a partial run, or a token-renew refresh) can add, remove or freeze elements:
  // drop what no longer exists and re-derive the invariants rather than acting on a stale selection.
  const lastInventory = useRef(inventory);
  useEffect(() => {
    if (lastInventory.current === inventory) {
      return;
    }
    lastInventory.current = inventory;
    setSelection((previous) => reconcileInventory(previous, inventory, types));
  }, [inventory, types]);

  // The DKIM selector list is an SSH read on the mail host, so it is fetched **once**, when the email
  // step is first entered, and kept here: a step body would lose it (PatternFly unmounts inactive
  // steps) and re-run the read on every visit.
  const [emailStepEntered, setEmailStepEntered] = useState(false);
  const mailDomainId = resources.email_hosting?.mail_domain.id;
  const { data: dkim, loading: dkimFetching } = useDkimSelectors(
    emailStepEntered && mailDomainId !== undefined ? mailDomainId : undefined,
  );

  const handleStepChange = useCallback((_event: unknown, currentStep: WizardStepType) => {
    if (currentStep.id === DELETE_RESOURCES_STEP_IDS.type('email_hosting')) {
      setEmailStepEntered(true);
    }
  }, []);

  const handleToggleType = useCallback(
    (key: DomainResourceTypeKey, checked: boolean) => {
      setSelection((previous) => toggleType(previous, inventory, types, key, checked));
    },
    [inventory, types],
  );

  const handleToggleAllTypes = useCallback(
    (checked: boolean) => {
      setSelection((previous) => toggleAllTypes(previous, inventory, types, checked));
    },
    [inventory, types],
  );

  const handleToggleElement = useCallback(
    (key: DomainResourceTypeKey, elementKey: string, checked: boolean) => {
      setSelection((previous) => toggleElement(previous, inventory, types, key, elementKey, checked));
    },
    [inventory, types],
  );

  const handleToggleOrgDomain = useCallback(
    (checked: boolean) => {
      setSelection((previous) => toggleOrgDomain(previous, inventory, types, checked));
    },
    [inventory, types],
  );

  const handleToggleAllElements = useCallback(
    (key: DomainResourceTypeKey, checked: boolean) => {
      setSelection((previous) => toggleAllElements(previous, inventory, types, key, checked));
    },
    [inventory, types],
  );

  const review = useMemo(
    () => deriveReview(selection, inventory, resources, types),
    [selection, inventory, resources, types],
  );

  const deleteCount = Array.from(review.willDelete.values()).reduce((total, elements) => total + elements.length, 0);

  // Leaving step 1 is blocked only while there is something to pick and nothing is picked. When no type
  // is selectable at all (nothing hosted, everything forced-kept or still under development) the user
  // must still reach the review, which is where the organization domain itself is asked about.
  const anySelectable = types.some((type) => typeState(inventory, type) === 'selectable');
  const selectionIncomplete = anySelectable && selection.selectedTypes.size === 0;

  // The confirmation gate of the review step. Case- and whitespace-insensitive, since the point is to
  // make the user spell the domain out, not to test their typing.
  const [confirmName, setConfirmName] = useState('');
  const confirmMatches = confirmName.trim().toLowerCase() === domain.name.trim().toLowerCase();
  const somethingGoes = hasAnyAction(selection, inventory);
  const blockers = useMemo(() => orgDomainBlockersOf(selection, inventory, types), [selection, inventory, types]);
  // Rules the backend's pre-flight would reject: catching them here saves a guaranteed refusal.
  const violations = useMemo(
    () => crossTypeViolations(selection, inventory, resources),
    [selection, inventory, resources],
  );

  const disabledReason = !somethingGoes
    ? 'Select at least one resource to delete.'
    : violations.length > 0
      ? violations[0]
      : !confirmMatches
        ? `Type ${domain.name} to confirm.`
        : 'Deleting resources is not available.';

  const { run, running } = useOrganizationDomainTeardown();
  const { addNotification, setDrawerOpen } = useNotifications();
  const [result, setResult] = useState<OrganizationDomainTeardownResult | null>(null);
  // A refusal the backend answered before running anything: the selection is still valid input, so the
  // user stays on the review step with the reason above the footer.
  const [submitError, setSubmitError] = useState<{ title: string; message: string; details: string[] } | null>(null);
  const [retryable, setRetryable] = useState(false);

  const submit = useCallback(async () => {
    setSubmitError(null);
    setRetryable(false);
    const outcome = await run(domain.id, toTeardownRequest(selection, inventory, confirmName));

    if (outcome.kind === 'executed') {
      setResult(outcome.teardown);
      const failures = outcome.teardown.steps.filter((step) => step.outcome === 'failed').length;
      addNotification({
        title: outcome.teardown.result === 'partial' ? 'Some resources were not deleted' : 'Resources deleted',
        srTitle: outcome.teardown.result === 'partial' ? 'Warning alert' : 'Success alert',
        description:
          outcome.teardown.result === 'partial'
            ? `The run for ${domain.name} stopped after ${failures === 1 ? 'a failure' : 'a failure'}; the remaining steps were not attempted.`
            : `Everything selected for ${domain.name} was removed.`,
        variant: outcome.teardown.result === 'partial' ? 'warning' : 'success',
      });
      setDrawerOpen(true);
      return;
    }

    if (outcome.kind === 'rejected') {
      // The kill switch is not a verdict on the selection — the feature itself is off, and no amount
      // of editing or retrying helps. Its message is a fixed string in the contract.
      const featureOff = outcome.message === 'Resource deletion is not enabled';
      setSubmitError({
        title: featureOff ? 'Deleting resources is turned off' : 'This selection was refused',
        message: featureOff
          ? 'Resource deletion is disabled on this environment. Nothing was changed. An Ancalagon operator has to enable it.'
          : outcome.message,
        details: [
          ...(outcome.blockedBy ?? []),
          ...(outcome.invalid ?? []).map((entry) => `${entry.type} #${entry.id}: ${entry.reason}`),
        ],
      });
    } else {
      setSubmitError({ title: 'The deletion could not be sent', message: outcome.message, details: [] });
      setRetryable(true);
    }

    addNotification({
      title: 'Resources were not deleted',
      srTitle: 'Error alert',
      description: outcome.message,
      variant: 'danger',
    });
    setDrawerOpen(true);
  }, [addNotification, confirmName, domain.id, domain.name, inventory, run, selection, setDrawerOpen]);

  // "Delete more resources" after a run: the inventory changed, so re-read it and start from step 1
  // with whatever is left. The reconcile effect above drops anything that is now gone.
  const startOver = useCallback(() => {
    setResult(null);
    setSubmitError(null);
    setConfirmName('');
    setSelection(EMPTY_WIZARD_SELECTION);
    onRefetchResources?.();
  }, [onRefetchResources]);

  if (result !== null) {
    return (
      <DeleteResourcesResult
        domain={domain}
        result={result}
        onRetry={() => void submit()}
        onStartOver={startOver}
        submitting={running}
      />
    );
  }

  return (
    <>
      <Wizard navAriaLabel="Delete resources steps" onClose={onCancel} onStepChange={handleStepChange} isVisitRequired>
        <WizardStep
          name="Resources"
          id={DELETE_RESOURCES_STEP_IDS.selection}
          footer={{ isNextDisabled: selectionIncomplete }}
        >
          <ResourceTypeSelectionStep
            domainName={domain.name}
            types={types}
            inventory={inventory}
            selection={selection}
            onToggleType={handleToggleType}
            onToggleAll={handleToggleAllTypes}
          />
        </WizardStep>

        {types.map((type) => {
          const StepBody = STEP_COMPONENTS[type.key];
          const elements = inventory.elements.get(type.key) ?? [];
          return (
            <WizardStep
              key={type.key}
              name={type.label}
              id={DELETE_RESOURCES_STEP_IDS.type(type.key)}
              // Always rendered so step indices stay stable; hidden unless the type is both selectable and
              // selected (PatternFly skips hidden steps on Next/Back and drops their nav item).
              isHidden={typeState(inventory, type) !== 'selectable' || !selection.selectedTypes.has(type.key)}
            >
              <StepBody
                domain={domain}
                resources={resources}
                type={type}
                elements={elements}
                selectedKeys={selection.selectedElements.get(type.key) ?? new Set<string>()}
                onToggle={(elementKey, checked) => handleToggleElement(type.key, elementKey, checked)}
                onToggleAll={(checked) => handleToggleAllElements(type.key, checked)}
                dkim={dkim}
                dkimLoading={emailStepEntered && dkimFetching && dkim === null}
                mailDomainGoes={mailDomainGoesOf(selection, inventory)}
              />
            </WizardStep>
          );
        })}

        <WizardStep
          name="Review"
          id={DELETE_RESOURCES_STEP_IDS.review}
          footer={
            <DeleteResourcesReviewFooter
              deleteCount={deleteCount + (selection.deleteOrgDomain ? 1 : 0)}
              canConfirm={somethingGoes && confirmMatches && violations.length === 0}
              disabledReason={disabledReason}
              submitting={running}
              onConfirm={() => void submit()}
            />
          }
        >
          <DeleteResourcesReviewStep
            domain={domain}
            types={types}
            inventory={inventory}
            review={review}
            orgDomainBlockers={blockers}
            violations={violations}
            deleteOrgDomain={selection.deleteOrgDomain}
            onToggleOrgDomain={handleToggleOrgDomain}
            confirmName={confirmName}
            onConfirmNameChange={setConfirmName}
            confirmMatches={confirmMatches}
            dkim={dkim}
            stepIdOf={DELETE_RESOURCES_STEP_IDS.type}
            selectionStepId={DELETE_RESOURCES_STEP_IDS.selection}
            submitError={submitError}
            onRetrySubmit={retryable ? () => void submit() : undefined}
          />
        </WizardStep>
      </Wizard>

      {/* The teardown is one synchronous request that can take a while (IMAP purges, a zone delete, a
          ZeroSSL cancel), so the click is bridged by a non-dismissible please-wait modal. As in the
          trade wizard, `disableFocusTrap` is load-bearing: the modal has no focusable element. */}
      <Modal variant="small" isOpen={running} aria-label="Deleting resources" disableFocusTrap>
        <ModalHeader title="Deleting resources" />
        <ModalBody>
          <Flex
            direction={{ default: 'column' }}
            alignItems={{ default: 'alignItemsCenter' }}
            spaceItems={{ default: 'spaceItemsMd' }}
          >
            <FlexItem>
              <Spinner size="xl" aria-label="Deleting resources" />
            </FlexItem>
            <FlexItem>
              <Content component="p" className="pf-v6-u-text-align-center">
                Removing the selected resources for {domain.name}. This can take a minute — please keep this window
                open.
              </Content>
            </FlexItem>
          </Flex>
        </ModalBody>
      </Modal>
    </>
  );
};

export default DeleteResourcesWizard;
